import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { adminPart2, adminUpdatePart2, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Part2Responses from "@/components/part2/Part2Responses";
import Part2Tools from "@/components/part2/Part2Tools";
import { useApp } from "@/context/AppContext";

const LANGS = ["en", "nl", "bg", "uk"];

function LangField({ label, value, onChange, testid, area }) {
  const Tag = area ? "textarea" : "input";
  return (
    <div className="rbl-field"><label className="rbl-label">{label}</label>
      <Tag className="rbl-input" rows={area ? 7 : undefined} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>
  );
}

export default function AdminPart2() {
  const { user } = useApp();
  const [days, setDays] = useState([]);
  const [sel, setSel] = useState(1);
  const [lang, setLang] = useState("nl");
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminPart2().then(setDays).catch(() => {}); }, []);

  const day = days.find((d) => d.day_number === sel);
  const patch = (fn) => setDays(days.map((d) => (d.day_number === sel ? fn({ ...d }) : d)));
  const setLangVal = (key, v) => patch((d) => ({ ...d, [key]: { ...d[key], [lang]: v } }));
  const setSec = (i, key, v) => patch((d) => ({ ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, [key]: { ...s[key], [lang]: v } } : s)) }));
  const addSec = () => patch((d) => ({ ...d, sections: [...(d.sections || []), { heading: {}, body: {} }] }));
  const delSec = (i) => window.confirm("Remove this section (all languages)?") && patch((d) => ({ ...d, sections: d.sections.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await Promise.all(days.map((d) => adminUpdatePart2(d.day_number, { title: d.title, subtitle: d.subtitle, hours: d.hours, sections: d.sections || [] })));
      toast.success("All 4 days saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <h1 className="rbl-h1" style={{ fontSize: 34 }}>Part 2 — training days</h1>
      <p style={{ color: "var(--muted)" }}>Only groups with “Part 2 access” (Groups page) see this. Day content (overview, check-in, theory, quiz, worksheets, assignment, evaluation) is built in; sections you add here appear in the Theory tab under “Extra from the trainer”. Days without built-in content stay locked for participants. Empty languages fall back to English.</p>
      {["admin", "trainer"].includes(user?.role) && <Part2Tools />}
      <div className="rbl-langs" style={{ marginTop: 14 }}>
        {days.map((d) => (
          <button key={d.day_number} className={`rbl-lang-btn ${sel === d.day_number ? "active" : ""}`} data-testid={`p2-admin-day-${d.day_number}`} onClick={() => setSel(d.day_number)}>Day {d.day_number}</button>
        ))}
      </div>
      <div className="rbl-langs" style={{ marginTop: 10 }}>
        {LANGS.map((l) => (
          <button key={l} className={`rbl-lang-btn ${lang === l ? "active" : ""}`} data-testid={`p2-admin-lang-${l}`} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
        ))}
      </div>
      {day && (
        <div style={{ marginTop: 18 }} data-testid="p2-admin-editor">
          <LangField label={`Title (${lang.toUpperCase()})`} value={day.title?.[lang]} onChange={(v) => setLangVal("title", v)} testid="p2-admin-title" />
          <LangField label={`Subtitle (${lang.toUpperCase()})`} value={day.subtitle?.[lang]} onChange={(v) => setLangVal("subtitle", v)} testid="p2-admin-subtitle" />
          <LangField label="Hours" value={day.hours} onChange={(v) => patch((d) => ({ ...d, hours: v }))} testid="p2-admin-hours" />
          {(day.sections || []).map((s, i) => (
            <div key={i} className="rbl-feature" style={{ marginTop: 14 }} data-testid={`p2-admin-section-${i}`}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><strong>Section {i + 1}</strong>
                <button className="rbl-icon-btn danger" onClick={() => delSec(i)} data-testid={`p2-admin-del-section-${i}`}><Trash2 size={16} /></button></div>
              <LangField label={`Heading (${lang.toUpperCase()})`} value={s.heading?.[lang]} onChange={(v) => setSec(i, "heading", v)} testid={`p2-admin-heading-${i}`} />
              <LangField area label={`Text (${lang.toUpperCase()}) — empty line = new paragraph`} value={s.body?.[lang]} onChange={(v) => setSec(i, "body", v)} testid={`p2-admin-body-${i}`} />
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="rbl-btn" onClick={addSec} data-testid="p2-admin-add-section"><Plus size={16} /> Add section</button>
            <button className="rbl-btn green" onClick={save} disabled={busy} data-testid="p2-admin-save">{busy ? "Saving…" : "Save all days"}</button>
          </div>
        </div>
      )}
      {["admin", "trainer"].includes(user?.role) && <Part2Responses day={sel} />}
    </AdminLayout>
  );
}
